import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, Paper, Stack, Table, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

// Phase HR-2a: الحضور — derived from POS shift clock-ins for employees
// linked to a system user. No extra hardware, no double entry.

interface AttendanceDayDto {
  employeeId: string;
  employeeName: string;
  day: string;
  firstClockIn: string;
  lastClockOut: string | null;
  hoursWorked: number;
  shiftCount: number;
}

interface AttendanceReportDto {
  from: string;
  to: string;
  rows: AttendanceDayDto[];
}

export function AttendancePage() {
  const { t } = useTranslation();
  const [range, setRange] = useState<[Date | null, Date | null]>([
    new Date(Date.now() - 6 * 86_400_000),
    new Date(),
  ]);

  const fromIso = range[0] ? toIsoDate(range[0]) : toIsoDate(new Date());
  const toIso = range[1] ? toIsoDate(range[1]) : toIsoDate(new Date());

  const { data, isLoading } = useQuery({
    queryKey: ['attendance', fromIso, toIso] as const,
    queryFn: async () =>
      (
        await apiClient.get<AttendanceReportDto>('/api/hr/attendance', {
          params: { from: fromIso, to: toIso },
        })
      ).data,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.hr')}
        title={t('hr.attendance.title')}
        subtitle={t('hr.attendance.subtitle')}
      />

      <Paper p="md">
        <DatePickerInput
          type="range"
          value={range}
          onChange={setRange}
          style={{ maxWidth: 300 }}
        />
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('hr.attendance.columns.day')}</Table.Th>
                <Table.Th>{t('hr.attendance.columns.employee')}</Table.Th>
                <Table.Th>{t('hr.attendance.columns.in')}</Table.Th>
                <Table.Th>{t('hr.attendance.columns.out')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('hr.attendance.columns.hours')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data?.rows ?? []).map((r) => (
                <Table.Tr key={`${r.employeeId}-${r.day}`}>
                  <Table.Td className="font-numeric">{r.day}</Table.Td>
                  <Table.Td>{r.employeeName}</Table.Td>
                  <Table.Td className="font-numeric">{timeOf(r.firstClockIn)}</Table.Td>
                  <Table.Td className="font-numeric">
                    {r.lastClockOut ? timeOf(r.lastClockOut) : t('hr.attendance.stillOpen')}
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 600 }}>
                    {r.hoursWorked.toFixed(2)}
                  </Table.Td>
                </Table.Tr>
              ))}
              {data && data.rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      {t('hr.attendance.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

function timeOf(iso: string): string {
  return iso.slice(11, 16);
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
